/**
 * The Search Progress tab's two stage series, derived from ONE wire field.
 *
 * `stage_transitions_by_day` is sparse in both dimensions — a (day, stage) pair
 * with no entries is simply absent, and a day on which nothing at all happened
 * has no row of any kind. That is the right shape to store (eleven live stages
 * zero-filled across a year would be ~4,000 rows of mostly zeros) and the wrong
 * shape to plot: a line chart reads a missing point as "we skipped this day",
 * not as "nobody moved". So the zero-fill happens here, once, and both charts
 * share it.
 *
 * The cumulative chart is DERIVED, never fetched. Two series on the wire that
 * are supposed to be a sum of each other is two things that can disagree; one
 * series and a fold cannot.
 *
 * The baseline is what makes the fold honest. Both daily series are capped at
 * 366 days, so a search open for three years shows its last year — and a stage
 * fourteen candidates entered in year one would start its cumulative line at
 * zero and under-report itself across the entire visible range. 01 ships
 * `stage_transitions_before_window` for exactly this, and anything this module's
 * own clamp drops off the front is added to it rather than lost.
 */

import type { SearchReportStageDay } from "@nu-assist/contracts";

import { dayRange, isDayKey } from "./dayAxis";

/**
 * The plotted window, in days.
 *
 * The backend already caps at 366 (`MAX_DAILY_SERIES_DAYS`), so this is a second
 * belt rather than the primary one: the report is CACHED, and an item composed
 * under an older cap — or one carrying a row with a typo'd year — reaches this
 * code as data, not as a deploy. Keep the two numbers equal; if the backend's
 * cap ever moves, the visible effect of them disagreeing is a chart that quietly
 * plots less than the report contains.
 */
export const MAX_PLOTTED_DAYS = 366;

/** One stage's value on every day of the window. */
export interface StageSeries {
  stage: string;
  /** One entry per day in `StageProgress.days`, same order, zero-filled. */
  counts: number[];
  /**
   * The number the legend shows. For the daily series that is the sum over the
   * window; for the cumulative series it is the final value, which is the total
   * that ever entered the stage including the baseline.
   */
  total: number;
}

export interface StageProgress {
  /** Contiguous `YYYY-MM-DD`, ascending. Empty when there is nothing to plot. */
  days: string[];
  /** Ordered biggest first, then by name, so the legend reads as a ranking. */
  series: StageSeries[];
  /** Entries per stage that predate `days[0]` — the wire baseline plus anything the clamp dropped. */
  baseline: Record<string, number>;
}

/**
 * Fold the sparse (day, stage) rows into a zero-filled series per stage.
 *
 * Rows with an unparseable day are DROPPED rather than coerced: Thrive validates
 * nothing on these dates, and a row that cannot be placed on the axis has no
 * honest position to occupy. They are not folded into the baseline either —
 * "before the window" is a claim about time, and an unparseable date makes no
 * claim at all.
 */
export function stageProgressFrom(
  transitions: readonly SearchReportStageDay[],
  beforeWindow: Readonly<Record<string, number>> = {},
): StageProgress {
  const baseline: Record<string, number> = {};
  for (const [stage, count] of Object.entries(beforeWindow)) {
    if (count > 0) baseline[stage] = count;
  }

  const usable = transitions.filter((row) => isDayKey(row.day) && row.count > 0);
  if (usable.length === 0) return { days: [], series: [], baseline };

  let first = usable[0].day;
  let last = usable[0].day;
  for (const row of usable) {
    if (row.day < first) first = row.day; // ISO days sort lexicographically
    if (row.day > last) last = row.day;
  }

  const days = dayRange(first, last, MAX_PLOTTED_DAYS);
  if (days.length === 0) return { days: [], series: [], baseline };
  const windowStart = days[0];
  const dayIndex = new Map(days.map((day, index) => [day, index]));

  const counts = new Map<string, number[]>();
  for (const row of usable) {
    if (row.day < windowStart) {
      baseline[row.stage] = (baseline[row.stage] ?? 0) + row.count;
      continue;
    }
    let series = counts.get(row.stage);
    if (!series) {
      series = new Array<number>(days.length).fill(0);
      counts.set(row.stage, series);
    }
    // Non-null: `row.day` is inside the window and the window is contiguous.
    series[dayIndex.get(row.day) as number] += row.count;
  }

  const series = [...counts.entries()]
    .map(([stage, values]) => ({
      stage,
      counts: values,
      total: values.reduce((sum, n) => sum + n, 0),
    }))
    .sort((a, b) => b.total - a.total || a.stage.localeCompare(b.stage));

  return { days, series, baseline };
}

/**
 * The running total per stage — "total candidates in each stage by day".
 *
 * Starts each line at its baseline rather than at zero, which is the whole point
 * of the baseline field. A stage that appears ONLY in the baseline (everyone
 * entered it before the window) still gets a line, flat at its baseline value:
 * omitting it would tell the reader the search has no Reference candidates when
 * it has three, which is a stronger claim than the chart is entitled to make.
 */
export function cumulativeFrom(progress: StageProgress): StageSeries[] {
  if (progress.days.length === 0) return [];

  const stages = new Set<string>(progress.series.map((s) => s.stage));
  for (const [stage, count] of Object.entries(progress.baseline)) {
    if (count > 0) stages.add(stage);
  }

  const daily = new Map(progress.series.map((s) => [s.stage, s.counts]));
  return [...stages]
    .map((stage) => {
      let running = progress.baseline[stage] ?? 0;
      const perDay = daily.get(stage);
      const counts = progress.days.map((_, index) => {
        running += perDay?.[index] ?? 0;
        return running;
      });
      return { stage, counts, total: counts[counts.length - 1] };
    })
    .sort((a, b) => b.total - a.total || a.stage.localeCompare(b.stage));
}

/** Total entries that predate the plotted window, across every stage. */
export function baselineTotal(progress: StageProgress): number {
  return Object.values(progress.baseline).reduce((sum, n) => sum + n, 0);
}
